import { VStack, HStack, Box, Alert, AlertDescription, AlertIcon, AlertTitle } from '@chakra-ui/react';
import Header from '../components/Header';
import DashboardHeader from '../components/DashboardHeader';
import HelpMessage from '../components/HelpMessage';
import AuthContext from '../contexts/AuthContext';
import MerchantContext from '../contexts/MerchantContext';
import InvoiceDataContext from '../contexts/InvoiceDataContext';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PaymentModalStatus from '../components/PaymentModalStatus';
import useQueryStringParams from '../hooks/useQueryStringParams';
import PopupModal from '../components/PopupModal';
import PalommaPlusPopup from '../components/PalommaPlusPopup';
import InvoicesTabAndStepperLayout from '../components/InvoicesTabAndStepperLayout';
import Footer from '../components/Footer';
import { useBreakpointsContext } from '../hooks/useBreakpointsContext';
import PaymentModalStatusContext from '../contexts/PaymentModalStatusContext';
import { useInvoices } from '../hooks/useInvoices';

const AppContainer = (): JSX.Element => {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const { merchant } = useContext(MerchantContext);
  const { setInvoiceIds: setPaymentModalInvoiceIds } = useContext(PaymentModalStatusContext);
  const { setInvoiceData } = useContext(InvoiceDataContext);
  const { isBlockedForCardPayments, cardFeePercentage } = useInvoices();
  const { id: queryId } = useQueryStringParams(['id']);
  const { isSmallScreen } = useBreakpointsContext();

  // Sync useInvoices data to InvoiceDataContext
  useEffect(() => {
    setInvoiceData({
      isBlockedForCardPayments,
      cardFeePercentage,
    });
  }, [isBlockedForCardPayments, cardFeePercentage, setInvoiceData]);

  useEffect(() => {
    if (queryId) setPaymentModalInvoiceIds(queryId.split(','));
  }, [queryId, setPaymentModalInvoiceIds]);

  return (
    <>
      <AlertBanner />
      <VStack height={'100vh'} gap={'24px'} minWidth={'320px'}>
        <Header
          id={authCtx.user}
          logout={() => {
            if (authCtx.orgId && authCtx.orgId === merchant?.org?.id) {
              // Navigating in the same handler batches with logout, so the merchant login never renders.
              navigate(`/org/${authCtx.orgId}`);
              authCtx.logout();
            } else if (merchant?.redirectOnLogout) window.location.replace(merchant.redirectOnLogout);
            else authCtx.logout();
          }}
        />
        <Box width={'100%'} px={[4, 4, '60px']} display={'flex'} flexDirection={'column'} gap={isSmallScreen ? 4 : ''}>
          <HStack justifyContent={'space-between'}>
            <DashboardHeader />
            {!isSmallScreen && <HelpMessage />}
          </HStack>
          <InvoicesTabAndStepperLayout />
          <Footer />
        </Box>
      </VStack>

      <PaymentModalStatus />

      {merchant?.popupImage && (
        <PopupModal
          img={merchant.popupImage.imageUrl}
          redirectUrl={merchant.popupImage.redirectUrl}
          localStorageKey="merchantInfoModal"
          displayOncePer="day"
        />
      )}
      <PalommaPlusPopup />
    </>
  );
};

const AlertBanner = (): JSX.Element => {
  const { merchant } = useContext(MerchantContext);

  if (!merchant?.paymentsPortalBanner) return <></>;
  const banner = merchant.paymentsPortalBanner;
  return (
    <Alert
      status={banner.type || 'warning'}
      width="100%"
      minHeight={'46.9px'}
      cursor={banner.link ? 'pointer' : 'default'}
      onClick={() => {
        if (banner.link) window.open(banner.link, '_blank');
      }}
    >
      <AlertIcon />
      <AlertTitle>{banner.title}</AlertTitle>
      <AlertDescription>{banner.text}</AlertDescription>
    </Alert>
  );
};

export default AppContainer;
